import { isGlobalViewer } from '../../../constants/roles';
// src/modules/treasury/pages/BranchCashLedger.jsx
// Branch cash ledger: date-filtered, branch-scoped view of all cash movements.
// HO users can select any branch; branch users see only their own branch.
// Two tabs: Cash Ledger (cash only) | All Ledger (cash + bank).

import React, { useEffect, useState, useCallback } from 'react';
import {
  Card, Table, DatePicker, Select, Row, Col, Statistic, Tag,
  Space, Button, Typography, Spin, Alert, Divider, Tooltip, Popover, Tabs,
} from 'antd';
import { ReloadOutlined, BankOutlined, InfoCircleOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { useAuth } from '../../../context/AuthContext';
import { fetchBranchById, fetchBranches } from '../../branches/api';
import { fetchCashLedger, fetchCombinedLedger } from '../api';
import { CASH_LEDGER_ENTRY_TYPE, TREASURY_MODULE } from '../constants/treasuryConstants';

const { Title, Text } = Typography;

const fmt = (v) => Number((v || 0).toFixed(2)).toLocaleString('en-IN');

const MODULE_LABELS = {
  [TREASURY_MODULE.WALK_IN]: 'Walk-In',
  [TREASURY_MODULE.PLEDGE_ADVANCE]: 'Pledge Advance',
  [TREASURY_MODULE.PLEDGE_SETTLEMENT]: 'Pledge Settlement',
  [TREASURY_MODULE.MANUAL_ADJUSTMENT]: 'Adjustment',
  [TREASURY_MODULE.BRANCH_FUNDING]: 'Branch Funding',
  [TREASURY_MODULE.EOD]: 'EOD',
};

const APPROVAL_STATUS_COLOR = {
  PENDING: 'orange',
  APPROVED: 'green',
  AUTO_APPROVED: 'cyan',
  REJECTED: 'red',
};

const CASE_MODULE_SET = new Set([
  TREASURY_MODULE.WALK_IN,
  TREASURY_MODULE.PLEDGE_SETTLEMENT,
  TREASURY_MODULE.PLEDGE_ADVANCE,
]);

const ENTRY_TYPE_COLORS = {
  [CASH_LEDGER_ENTRY_TYPE.OPENING_BALANCE]: 'blue',
  [CASH_LEDGER_ENTRY_TYPE.CASH_IN]: 'green',
  [CASH_LEDGER_ENTRY_TYPE.CASH_OUT]: 'red',
  [CASH_LEDGER_ENTRY_TYPE.ADJUSTMENT]: 'orange',
  [CASH_LEDGER_ENTRY_TYPE.CLOSING_BALANCE]: 'default',
  BANK_OUT: 'purple',
};

const INFLOW_TYPES = [
  CASH_LEDGER_ENTRY_TYPE.OPENING_BALANCE,
  CASH_LEDGER_ENTRY_TYPE.CASH_IN,
  CASH_LEDGER_ENTRY_TYPE.ADJUSTMENT,
];

// A CASH_IN under a customer-payout module is a reversal (payout returned to the
// drawer), not genuine new cash in — matches the backend summary classification.
const CASH_PAYOUT_MODULES = new Set([
  TREASURY_MODULE.WALK_IN,
  TREASURY_MODULE.PLEDGE_SETTLEMENT,
  TREASURY_MODULE.PLEDGE_ADVANCE,
]);
const isPayoutReversal = (row) =>
  row.entryType === CASH_LEDGER_ENTRY_TYPE.CASH_IN && CASH_PAYOUT_MODULES.has(row.module);

export default function BranchCashLedger() {
  const { user } = useAuth();

  const [userBranch, setUserBranch] = useState(null);
  const [isHO, setIsHO] = useState(false);
  const [branches, setBranches] = useState([]);
  const [activeTab, setActiveTab] = useState('cash');

  const [selectedBranchId, setSelectedBranchId] = useState(null);
  const [selectedDate, setSelectedDate] = useState(dayjs());

  // Cash tab state
  const [cashEntries, setCashEntries] = useState([]);
  const [cashSummary, setCashSummary] = useState(null);
  const [cashPagination, setCashPagination] = useState({ current: 1, pageSize: 50, total: 0 });
  const [cashLoading, setCashLoading] = useState(false);

  // All Ledger tab state
  const [combinedEntries, setCombinedEntries] = useState([]);
  const [combinedSummary, setCombinedSummary] = useState(null);
  const [combinedPagination, setCombinedPagination] = useState({ current: 1, pageSize: 50, total: 0 });
  const [combinedLoading, setCombinedLoading] = useState(false);

  const [initLoading, setInitLoading] = useState(true);
  const [error, setError] = useState(null);

  // ── Initialise: resolve user's branch type ─────────────────────────────────
  useEffect(() => {
    (async () => {
      try {
        if (!user?.branch) { setInitLoading(false); return; }
        const branchRes = await fetchBranchById(user.branch);
        const branch = branchRes?.data || branchRes;
        setUserBranch(branch);

        const ho = branch?.branchType === 'HO' || isGlobalViewer(user);
        setIsHO(ho);

        if (ho) {
          const bRes = await fetchBranches({ limit: 100 });
          const list = bRes?.data?.data || bRes?.data || [];
          setBranches(list);
        } else {
          setSelectedBranchId(user.branch);
        }
      } catch (err) {
        setError('Failed to load branch profile');
      } finally {
        setInitLoading(false);
      }
    })();
  }, [user]);

  // ── Fetch cash ledger ──────────────────────────────────────────────────────
  const fetchCashTab = useCallback(async (page = 1) => {
    const branchId = selectedBranchId || (isHO ? null : user?.branch);
    setCashLoading(true);
    setError(null);
    try {
      const params = {
        page,
        limit: cashPagination.pageSize,
        businessDate: selectedDate ? selectedDate.format('YYYY-MM-DD') : undefined,
      };
      if (branchId) params.branchId = branchId;

      const res = await fetchCashLedger(params);
      const data = res?.data;
      setCashEntries(data?.data || []);
      setCashSummary(data?.summary || null);
      setCashPagination(prev => ({
        ...prev,
        current: data?.pagination?.current || 1,
        total: data?.pagination?.total || 0,
      }));
    } catch (err) {
      setError(err.message || 'Failed to load cash ledger');
    } finally {
      setCashLoading(false);
    }
  }, [selectedBranchId, selectedDate, isHO, user, cashPagination.pageSize]);

  // ── Fetch combined ledger ──────────────────────────────────────────────────
  const fetchCombinedTab = useCallback(async (page = 1) => {
    const branchId = selectedBranchId || (isHO ? null : user?.branch);
    setCombinedLoading(true);
    setError(null);
    try {
      const params = {
        page,
        limit: combinedPagination.pageSize,
        businessDate: selectedDate ? selectedDate.format('YYYY-MM-DD') : undefined,
      };
      if (branchId) params.branchId = branchId;

      const res = await fetchCombinedLedger(params);
      const data = res?.data;
      setCombinedEntries(data?.data || []);
      setCombinedSummary(data?.summary || null);
      setCombinedPagination(prev => ({
        ...prev,
        current: data?.pagination?.current || 1,
        total: data?.pagination?.total || 0,
      }));
    } catch (err) {
      setError(err.message || 'Failed to load combined ledger');
    } finally {
      setCombinedLoading(false);
    }
  }, [selectedBranchId, selectedDate, isHO, user, combinedPagination.pageSize]);

  useEffect(() => {
    if (!initLoading) {
      fetchCashTab(1);
      fetchCombinedTab(1);
    }
  }, [selectedBranchId, selectedDate, initLoading]);

  const handleRefresh = () => {
    fetchCashTab(1);
    fetchCombinedTab(1);
  };

  const handleTabChange = (key) => {
    setActiveTab(key);
  };

  // ── Cash ledger columns ────────────────────────────────────────────────────
  const cashColumns = [
    {
      title: 'Date',
      dataIndex: 'businessDate',
      width: 110,
      render: (v) => v ? dayjs(v).format('DD/MM/YYYY') : '—',
    },
    {
      title: 'Reference',
      dataIndex: 'reference',
      width: 130,
      render: (v) => <Text code style={{ fontSize: 12 }}>{v}</Text>,
    },
    {
      title: 'Module',
      dataIndex: 'module',
      width: 140,
      render: (v) => <Tag>{MODULE_LABELS[v] || v}</Tag>,
    },
    {
      title: 'Type',
      dataIndex: 'entryType',
      width: 130,
      render: (v) => <Tag color={ENTRY_TYPE_COLORS[v]}>{v?.replace(/_/g, ' ')}</Tag>,
    },
    {
      title: 'Description',
      dataIndex: 'description',
      ellipsis: true,
    },
    {
      title: 'Cash In',
      dataIndex: 'amount',
      width: 130,
      align: 'right',
      render: (v, row) => {
        if (!INFLOW_TYPES.includes(row.entryType)) return null;
        if (isPayoutReversal(row)) {
          return (
            <Tooltip title="Reversal — a customer payout was reversed and the cash returned to the drawer. Not counted as new Cash In.">
              <Text style={{ color: '#722ed1', fontWeight: 500 }}>↩ ₹{fmt(v)}</Text>
            </Tooltip>
          );
        }
        const text = <Text style={{ color: '#389e0d', fontWeight: 500 }}>₹{fmt(v)}</Text>;
        if (row.entryType === CASH_LEDGER_ENTRY_TYPE.CASH_IN && row.module === TREASURY_MODULE.BRANCH_FUNDING && row.denominations) {
          const content = (
            <div style={{ fontSize: 12 }}>
              {Object.entries(row.denominations).filter(([k, val]) => val > 0 && k !== 'totalPhysicalCash').map(([k, val]) => (
                <div key={k}>{k.replace('note', '₹')}: {val}</div>
              ))}
            </div>
          );
          return (
            <Popover content={content} title="Denominations">
              <span style={{ cursor: 'pointer', borderBottom: '1px dashed #389e0d' }}>{text}</span>
            </Popover>
          );
        }
        return text;
      },
    },
    {
      title: 'Cash Out',
      dataIndex: 'amount',
      width: 130,
      align: 'right',
      render: (v, row) =>
        row.entryType === CASH_LEDGER_ENTRY_TYPE.CASH_OUT
          ? <Text style={{ color: '#cf1322', fontWeight: 500 }}>₹{fmt(v)}</Text>
          : null,
    },
    {
      title: 'Balance',
      dataIndex: 'runningBalance',
      width: 140,
      align: 'right',
      render: (v) => (
        <Text strong style={{ color: v < 0 ? '#cf1322' : undefined }}>
          ₹{fmt(v)}
        </Text>
      ),
    },
    isHO && {
      title: 'Branch',
      dataIndex: ['branch', 'name'],
      width: 150,
      render: (v, row) => row.branch?.name || '—',
    },
  ].filter(Boolean);

  // ── Combined ledger columns ────────────────────────────────────────────────
  const combinedColumns = [
    {
      title: 'Date',
      dataIndex: 'businessDate',
      width: 100,
      render: (v) => v ? dayjs(v).format('DD/MM/YYYY') : '—',
    },
    {
      title: 'Reference',
      dataIndex: 'reference',
      width: 120,
      render: (v) => <Text code style={{ fontSize: 11 }}>{v}</Text>,
    },
    {
      title: 'Channel',
      dataIndex: 'channel',
      width: 72,
      render: (v) => <Tag color={v === 'BANK' ? 'purple' : 'cyan'} style={{ fontSize: 11 }}>{v}</Tag>,
    },
    {
      title: 'Module',
      dataIndex: 'module',
      width: 120,
      render: (v) => <Tag style={{ fontSize: 11 }}>{MODULE_LABELS[v] || v}</Tag>,
    },
    {
      title: 'Customer / Description',
      ellipsis: true,
      render: (_, row) => {
        const hasCaseInfo = row.customerName || row.caseReference;
        if (hasCaseInfo) {
          return (
            <span>
              {row.customerName && (
                <Text strong style={{ fontSize: 13 }}>{row.customerName}</Text>
              )}
              {row.caseReference && (
                <Text type="secondary" style={{ fontSize: 11, marginLeft: row.customerName ? 6 : 0 }}>
                  ({row.caseReference})
                </Text>
              )}
              {row.description && row.description !== row.customerName && (
                <div>
                  <Text type="secondary" style={{ fontSize: 11 }}>{row.description}</Text>
                </div>
              )}
            </span>
          );
        }
        return <Text style={{ fontSize: 12 }}>{row.description || '—'}</Text>;
      },
    },
    {
      title: 'Cash In',
      dataIndex: 'amount',
      width: 110,
      align: 'right',
      render: (v, row) => {
        if (row.channel === 'BANK' || !INFLOW_TYPES.includes(row.entryType)) return null;
        if (isPayoutReversal(row)) {
          return (
            <Tooltip title="Reversal — payout returned to the drawer. Not counted as new Cash In.">
              <Text style={{ color: '#722ed1', fontWeight: 500 }}>↩ ₹{fmt(v)}</Text>
            </Tooltip>
          );
        }
        return <Text style={{ color: '#389e0d', fontWeight: 500 }}>₹{fmt(v)}</Text>;
      },
    },
    {
      title: 'Cash Out',
      dataIndex: 'amount',
      width: 110,
      align: 'right',
      render: (v, row) => {
        if (row.channel === 'BANK') return null;
        return row.entryType === CASH_LEDGER_ENTRY_TYPE.CASH_OUT
          ? <Text style={{ color: '#cf1322', fontWeight: 500 }}>₹{fmt(v)}</Text>
          : null;
      },
    },
    {
      title: 'Bank Out',
      dataIndex: 'amount',
      width: 110,
      align: 'right',
      render: (v, row) => {
        if (row.channel !== 'BANK') return null;
        const isPending = row.approvalStatus === 'PENDING';
        return (
          <span>
            <Text style={{ color: isPending ? '#d46b08' : '#531dab', fontWeight: 500 }}>
              ₹{fmt(v)}
            </Text>
          </span>
        );
      },
    },
    {
      title: 'Status',
      width: 95,
      render: (_, row) => {
        if (row.channel !== 'BANK') return null;
        const label = row.approvalStatus === 'AUTO_APPROVED' ? 'Approved' : (row.approvalStatus || '—');
        return (
          <Tag color={APPROVAL_STATUS_COLOR[row.approvalStatus] || 'default'} style={{ fontSize: 11 }}>
            {label.replace(/_/g, ' ')}
          </Tag>
        );
      },
    },
    {
      title: 'Balance',
      dataIndex: 'runningBalance',
      width: 120,
      align: 'right',
      render: (v) =>
        v != null
          ? <Text strong style={{ color: v < 0 ? '#cf1322' : undefined }}>₹{fmt(v)}</Text>
          : <Text type="secondary">—</Text>,
    },
    {
      title: 'Branch',
      width: 140,
      render: (_, row) => {
        const b = row.branch;
        if (!b) return <Text type="secondary">—</Text>;
        return (
          <span>
            <Text style={{ fontSize: 12 }}>{b.name}</Text>
            <Text type="secondary" style={{ fontSize: 11, marginLeft: 4 }}>({b.code})</Text>
          </span>
        );
      },
    },
  ];

  if (initLoading) return <Spin style={{ display: 'block', marginTop: 80 }} />;

  const controls = (
    <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
      <Col>
        <Title level={5} style={{ margin: 0 }}>
          <BankOutlined style={{ marginRight: 8 }} />
          Branch Cash Ledger
        </Title>
        {userBranch && !isHO && (
          <Text type="secondary" style={{ fontSize: 12 }}>
            {userBranch.name} ({userBranch.code})
          </Text>
        )}
      </Col>
      <Col>
        <Space>
          {isHO && (
            <Select
              placeholder="All branches"
              allowClear
              showSearch
              style={{ width: 220 }}
              value={selectedBranchId}
              onChange={setSelectedBranchId}
              optionFilterProp="label"
              options={branches.map(b => ({
                value: b._id,
                label: `${b.name} (${b.code})`,
              }))}
            />
          )}
          <DatePicker
            value={selectedDate}
            onChange={setSelectedDate}
            format="DD/MM/YYYY"
            allowClear={false}
          />
          <Tooltip title="Refresh">
            <Button
              icon={<ReloadOutlined />}
              onClick={handleRefresh}
              loading={cashLoading || combinedLoading}
            />
          </Tooltip>
        </Space>
      </Col>
    </Row>
  );

  const renderSummaryCards = (summary, showBank = false) => {
    if (!summary) return null;
    const hasPending = showBank && (summary.totalBankPending > 0);
    const hasReversals = (summary.totalReversals || 0) > 0;

    const reversalCard = hasReversals
      ? [{
          title: 'Reversals (Returned)',
          value: fmt(summary.totalReversals),
          prefix: '₹',
          color: '#722ed1',
          tooltip: 'Customer payouts that were reversed — cash returned to the drawer. Shown separately so it never inflates Total Cash In. Balance already reflects it.',
        }]
      : [];

    const cards = showBank
      ? [
          { title: 'Opening Cash', value: fmt(summary.openingCash), prefix: '₹' },
          { title: 'Total Cash In', value: fmt(summary.totalCashIn), prefix: '₹', color: '#389e0d' },
          { title: 'Total Cash Out', value: fmt(summary.totalCashOut), prefix: '₹', color: '#cf1322' },
          ...reversalCard,
          { title: 'Bank Out (Approved)', value: fmt(summary.totalBankOut), prefix: '₹', color: '#531dab' },
          ...(hasPending
            ? [{ title: 'Bank Pending', value: fmt(summary.totalBankPending), prefix: '₹', color: '#d46b08' }]
            : []),
          { title: 'Cash Balance', value: fmt(summary.closingBalance), prefix: '₹', bold: true },
        ]
      : [
          { title: 'Opening Cash', value: fmt(summary.openingCash), prefix: '₹' },
          { title: 'Total Cash In', value: fmt(summary.totalCashIn), prefix: '₹', color: '#389e0d' },
          { title: 'Total Cash Out', value: fmt(summary.totalCashOut), prefix: '₹', color: '#cf1322' },
          ...reversalCard,
          { title: 'Closing Balance', value: fmt(summary.closingBalance), prefix: '₹', bold: true },
        ];

    const colSpan = showBank
      ? { xs: 12, sm: 8, lg: 4 }
      : (hasReversals ? { xs: 12, sm: 8, lg: 4 } : { xs: 12, sm: 6, lg: 6 });

    return (
      <>
        <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
          {cards.map((s) => (
            <Col {...colSpan} key={s.title}>
              <Card size="small" bordered>
                <Statistic
                  title={s.tooltip
                    ? <span>{s.title} <Tooltip title={s.tooltip}><InfoCircleOutlined style={{ color: '#8c8c8c' }} /></Tooltip></span>
                    : s.title}
                  value={s.value}
                  prefix={s.prefix}
                  valueStyle={{ color: s.color, fontWeight: s.bold ? 'bold' : undefined, fontSize: 16 }}
                />
              </Card>
            </Col>
          ))}
        </Row>
        <Divider style={{ margin: '0 0 16px' }} />
      </>
    );
  };

  return (
    <div style={{ padding: '16px 0' }}>
      {controls}

      {error && (
        <Alert type="error" message={error} closable style={{ marginBottom: 16 }} />
      )}

      <Tabs
        activeKey={activeTab}
        onChange={handleTabChange}
        items={[
          {
            key: 'cash',
            label: 'Cash Ledger',
            children: (
              <>
                {renderSummaryCards(cashSummary, false)}
                <Table
                  dataSource={cashEntries}
                  columns={cashColumns}
                  rowKey="_id"
                  loading={cashLoading}
                  size="small"
                  scroll={{ x: 900 }}
                  pagination={{
                    current: cashPagination.current,
                    pageSize: cashPagination.pageSize,
                    total: cashPagination.total,
                    showSizeChanger: true,
                    showTotal: (t) => `${t} entries`,
                    onChange: (page) => fetchCashTab(page),
                  }}
                  locale={{ emptyText: 'No cash entries for this date' }}
                />
              </>
            ),
          },
          {
            key: 'combined',
            label: isHO && !selectedBranchId ? 'All Ledger — All Branches' : 'All Ledger (Cash + Bank)',
            children: (
              <>
                {renderSummaryCards(combinedSummary, true)}
                <Table
                  dataSource={combinedEntries}
                  columns={combinedColumns}
                  rowKey="_id"
                  loading={combinedLoading}
                  size="small"
                  scroll={{ x: 1300 }}
                  rowClassName={(row) => row.channel === 'BANK' ? 'bank-row' : ''}
                  pagination={{
                    current: combinedPagination.current,
                    pageSize: combinedPagination.pageSize,
                    total: combinedPagination.total,
                    showSizeChanger: true,
                    showTotal: (t) => `${t} entries`,
                    onChange: (page) => fetchCombinedTab(page),
                  }}
                  locale={{ emptyText: 'No entries for this date' }}
                />
              </>
            ),
          },
        ]}
      />
    </div>
  );
}
